import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Text, sparkAssetUrl, sparkToast } from '@spark';
import { GlobalContext } from '@/app/providers/globalState';
import PerformanceFiltersRow from './PerformanceFiltersRow';
import RunBenchmarkModal from './RunBenchmarkModal';
import { getModelPerfPlatform, listModelPerfRuns } from '../../api/modelPerfRunsApi';
import MeasuredRunOutcomeBanner from './MeasuredRunOutcomeBanner';
import MeasuredRunsHistory from './MeasuredRunsHistory';
import PerformanceFiltersModal from './PerformanceFiltersModal';
import CostBanner from './CostBanner';
import PerformanceStatsTable from './PerformanceStatsTable';
import InfrastructureComparisonTable from './InfrastructureComparisonTable';
import {
  DEFAULT_COST_CONFIG,
  DEFAULT_PERF_FILTERS,
  countActiveFilters,
  fetchPerformanceMetrics,
  fetchPerformanceMetricsOptions,
  fetchBenchmarkAvgTiktoken,
} from './data/performanceMetricsApi';
import { EmptyStateCard } from '@/features/tasks/components/HomePage/EmptyStateCard';
import { mergeComparisonIntoMetrics } from './data/mergeComparisonMetrics';
import { deriveThroughputRowsFromLatency } from './data/derivedMetrics';
import {
  catalogColumnLabel,
  catalogEmptyStateText,
  describeMeasuredRunOutcome,
  latencyTableEmptyText,
  measuredColumnLabel,
  mergeMeasuredIntoLatencyRows,
  selectMeasuredRunForTable,
} from './data/measuredRunMetrics';
import {
  filtersEqual,
  reconcileFiltersWithCatalog,
} from './data/filterReconciliation';

const EMPTY_FILTERS = { concurrency: null, environments: [] };

/**
 * Top-level layout for the Performance tab. Hosts filter state, the modal,
 * the cost banner, and — based on how many environments the user has
 * configured — either the single-view latency table OR the Infrastructure
 * Comparison table.
 *
 * Cost configuration trigger placement is mode-dependent:
 *   - Single-environment view  -> rendered inside the cost banner,
 *                                 anchored to its right edge.
 *   - Comparison view (2+ envs) -> rendered in `PerformanceFiltersRow`
 *                                 to the left of "Filter Metrics" (no
 *                                 banner exists in comparison mode).
 *
 * Rendering rule:
 *   - 1 environment  -> single-view latency table (default page load),
 *                       plus a Throughput Statistics table when
 *                       `filters.concurrency > 1` (derived client-side
 *                       from the latency rows × concurrency).
 *   - 2+ environments -> Infrastructure Comparison table only.
 *
 * Compare column visibility (single-view mode):
 *   - No Compare: selection, or Compare: is teacher/file      -> hidden.
 *   - Compare: is a SpecificAI version resolvable to a base
 *     model that has performance metrics seeded               -> shown.
 *   - Otherwise (e.g. unseeded base model, backend 404)       -> hidden.
 *
 * Catalog numbers come from `/api/performance-metrics` (seeded
 * `performance_metrics` — a static platform baseline). Measured
 * `model_perf_runs` are listed separately and, when they match the selected
 * SKU / concurrency, fill a "Measured {preset}" column. An empty catalog is
 * expected for unseeded base models and is not a failed serving-perf run.
 * A completed washout (errors, null latencies) is called out instead of
 * looking like an empty successful scorecard.
 *
 * @param {Object} props
 * @param {string|null} [props.baseModelName]              Base model of the "To:" version.
 * @param {string|null} [props.comparisonBaseModelName]    Base model of the "Compare:" version (if numeric).
 * @param {string|null} [props.comparisonModelLabel]       Label for the comparison column header (e.g. "SpecificAI v2").
 * @param {('student'|'teacher')} [props.comparisonModelKind]
 *        How the comparison fetch should query the
 *        `performance_metrics` collection. `"teacher"` when the
 *        Compare: select is the usecase's teacher model, otherwise
 *        `"student"` (default). Teacher docs have no infra and the
 *        backend short-circuits the cost lookup for them.
 * @param {string} [props.currentModelLabel]               Label for the current column header.
 * @param {import('./data/performanceMetricsApi').PerformanceFilters} [props.filters]
 * @param {(f: import('./data/performanceMetricsApi').PerformanceFilters) => void} [props.onFiltersChange]
 * @param {import('./data/performanceMetricsApi').CostConfig} [props.costConfig]
 * @param {(c: import('./data/performanceMetricsApi').CostConfig) => void} [props.onCostConfigChange]
 * @param {string} [props.taskType] - Forwarded to the Infrastructure
 *        Comparison table to decide whether the derived Throughput / Cost
 *        per 1M tokens columns are shown.
 * @param {string|null} [props.clientId] - Used to load benchmark token stats.
 * @param {string|null} [props.llmUsecaseId]
 * @param {string[]|null} [props.benchmarkNames] - Evaluation benchmark names.
 */
export default function PerformanceTab({
  baseModelName = null,
  comparisonBaseModelName = null,
  comparisonModelLabel = null,
  comparisonModelKind = 'student',
  currentModelLabel = 'Specific AI V1',
  filters: filtersFromParent,
  onFiltersChange,
  costConfig: costConfigFromParent,
  onCostConfigChange,
  taskType = null,
  clientId = null,
  llmUsecaseId = null,
  modelVersion = null,
  benchmarkNames = null,
}) {
  const [internalFilters, setInternalFilters] = useState(() => ({
    ...DEFAULT_PERF_FILTERS,
    environments: DEFAULT_PERF_FILTERS.environments.map((e) => ({ ...e })),
  }));
  const [internalCostConfig, setInternalCostConfig] = useState(() => ({
    ...DEFAULT_COST_CONFIG,
  }));

  const filtersControlled =
    filtersFromParent !== undefined && typeof onFiltersChange === 'function';
  const filters = filtersControlled ? filtersFromParent : internalFilters;
  const setFilters = filtersControlled ? onFiltersChange : setInternalFilters;

  const costControlled =
    costConfigFromParent !== undefined && typeof onCostConfigChange === 'function';
  const costConfig = costControlled ? costConfigFromParent : internalCostConfig;
  const setCostConfig = costControlled ? onCostConfigChange : setInternalCostConfig;
  const { modelPerfRunStatus, handleWsMessage, realtimeResyncTick } = useContext(GlobalContext);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [runModalOpen, setRunModalOpen] = useState(false);
  const [runsRefreshToken, setRunsRefreshToken] = useState(0);
  const [platformSku, setPlatformSku] = useState(null);
  const liveStatus = modelPerfRunStatus?.[`${llmUsecaseId}_${modelVersion}`];
  const [measuredRuns, setMeasuredRuns] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [comparisonMetrics, setComparisonMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [benchmarkAvgTiktoken, setBenchmarkAvgTiktoken] = useState(null);
  const toastedWashoutRef = useRef(null);

  const benchmarkNamesKey = useMemo(
    () => (Array.isArray(benchmarkNames) ? benchmarkNames.join('\0') : ''),
    [benchmarkNames]
  );

  useEffect(() => {
    if (!clientId || !llmUsecaseId) {
      setBenchmarkAvgTiktoken(null);
      return undefined;
    }
    let cancelled = false;
    fetchBenchmarkAvgTiktoken({ clientId, llmUsecaseId, benchmarkNames })
      .then((avg) => {
        if (!cancelled) setBenchmarkAvgTiktoken(avg);
      })
      .catch(() => {
        if (!cancelled) setBenchmarkAvgTiktoken(null);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, llmUsecaseId, benchmarkNamesKey]);

  useEffect(() => {
    if (!clientId || !llmUsecaseId) {
      setMeasuredRuns([]);
      return undefined;
    }
    // A newer request (live status change, resync, refresh) supersedes this
    // one; its answer could replay an older run over the live entry.
    let cancelled = false;
    listModelPerfRuns({ clientId, llmUsecaseId, modelVersion }, (data) => {
      // An error answer reaches this callback too; it has no runs, so keep the ones shown.
      if (cancelled || !Array.isArray(data?.runs)) return;
      const runs = data.runs;
      setMeasuredRuns(runs);
      // The live run's final status update may have been missed while the
      // WebSocket was down.
      const liveRun = liveStatus?.run_id && runs.find((run) => run.run_id === liveStatus.run_id);
      if (liveRun && liveRun.status !== liveStatus.status) {
        handleWsMessage?.({
          ...liveStatus,
          action: 'updateModelPerfRunStatus',
          status: liveRun.status,
          error: liveRun.error,
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [clientId, llmUsecaseId, modelVersion, runsRefreshToken, liveStatus?.status, liveStatus?.run_id, realtimeResyncTick]);

  const isComparisonMode = (filters?.environments?.length || 0) > 1;
  const hasActiveFilters = useMemo(
    () => countActiveFilters(filters) > 0,
    [filters]
  );

  // Auto-reconcile filters against the seeded options catalog whenever the
  // active base model changes. Default filters (concurrency=1, length=short,
  // first static environment) often don't match the seed for a given base
  // model — e.g. if only (concurrency=10, AWS, A10G, g5.xlarge) is seeded,
  // the default would land on "No data". Reconciling pulls each dimension
  // toward the first seeded value when the current value isn't present, so
  // the page lands on a real combination instead of an empty table.
  //
  // We use refs for `filters` / `setFilters` so the effect re-runs only
  // when `baseModelName` flips — using them in the deps array would re-fire
  // the catalog fetch on every keystroke / unrelated render and risk
  // overriding intentional user edits.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const setFiltersRef = useRef(setFilters);
  setFiltersRef.current = setFilters;
  const platformSkuRef = useRef(platformSku);
  platformSkuRef.current = platformSku;

  useEffect(() => {
    let cancelled = false;
    getModelPerfPlatform((data) => {
      if (cancelled || !data?.cloud_provider) return;
      setPlatformSku(data);
      const current = filtersRef.current;
      if (!current?.environments?.length) return;
      const first = current.environments[0] || {};
      const nextEnv = {
        cloudProvider: data.cloud_provider,
        hardwareDevice: data.hardware_device || 'GPU',
        accelerator: data.accelerator || null,
        instanceType: data.instance_type,
      };
      if (
        first.cloudProvider === nextEnv.cloudProvider
        && first.instanceType === nextEnv.instanceType
      ) {
        return;
      }
      setFiltersRef.current({
        ...current,
        environments: [nextEnv, ...current.environments.slice(1)],
      });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!baseModelName) return undefined;
    let cancelled = false;
    fetchPerformanceMetricsOptions({ baseModelName })
      .then((options) => {
        if (cancelled) return;
        const current = filtersRef.current;
        // Skip reconcile when the user has cleared all filters (empty
        // state card is the intended UI in that case).
        if (!current || !(current.environments?.length)) return;
        const reconciled = reconcileFiltersWithCatalog(current, options, {
          preferredCloudProvider: platformSkuRef.current?.cloud_provider,
        });
        if (!filtersEqual(current, reconciled)) {
          setFiltersRef.current(reconciled);
        }
      })
      .catch(() => {
        // Catalog fetch failed -> leave filters as-is; the data fetch
        // below will still surface the catalog-empty copy if the combo
        // is unseeded.
      });
    return () => {
      cancelled = true;
    };
  }, [baseModelName, platformSku]);

  useEffect(() => {
    if (isComparisonMode || !baseModelName || !hasActiveFilters) {
      setMetrics(null);
      setComparisonMetrics(null);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    const primaryPromise = fetchPerformanceMetrics({
      baseModelName,
      filters,
      costConfig,
      currentModelLabel,
    });

    const comparisonPromise = comparisonBaseModelName
      ? fetchPerformanceMetrics({
        baseModelName: comparisonBaseModelName,
        filters,
        costConfig,
        currentModelLabel: comparisonModelLabel || 'Comparison',
        modelKind: comparisonModelKind,
      })
      : Promise.resolve(null);

    Promise.all([primaryPromise, comparisonPromise])
      .then(([primary, comparison]) => {
        if (cancelled) return;
        setMetrics(primary);
        setComparisonMetrics(comparison);
      })
      .catch(() => {
        if (cancelled) return;
        setMetrics({ found: false });
        setComparisonMetrics(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    baseModelName,
    comparisonBaseModelName,
    comparisonModelLabel,
    comparisonModelKind,
    filters,
    costConfig,
    currentModelLabel,
    isComparisonMode,
    hasActiveFilters,
  ]);

  const handleApply = (next) => {
    setFilters(next);
    setFiltersOpen(false);
  };

  const handleClear = () => setFilters(EMPTY_FILTERS);

  // Is comparison data actually available for this filter combination?
  const hasComparisonData = Boolean(
    comparisonBaseModelName && comparisonMetrics && comparisonMetrics.found !== false
  );

  const mergedMetrics = useMemo(
    () => mergeComparisonIntoMetrics(metrics, hasComparisonData ? comparisonMetrics : null),
    [metrics, comparisonMetrics, hasComparisonData]
  );

  const showComparison = hasComparisonData;

  const selectedMeasuredRun = useMemo(
    () => (isComparisonMode ? null : selectMeasuredRunForTable(measuredRuns, filters)),
    [isComparisonMode, measuredRuns, filters],
  );
  const measuredOutcome = useMemo(
    () => describeMeasuredRunOutcome(selectedMeasuredRun, filters?.concurrency),
    [selectedMeasuredRun, filters?.concurrency],
  );
  const showMeasured = Boolean(selectedMeasuredRun);
  const latencyRows = useMemo(
    () => mergeMeasuredIntoLatencyRows(
      mergedMetrics?.inferenceLatency,
      selectedMeasuredRun,
      filters?.concurrency,
    ),
    [mergedMetrics?.inferenceLatency, selectedMeasuredRun, filters?.concurrency],
  );
  const hasCatalogValues = useMemo(
    () => Array.isArray(latencyRows) && latencyRows.some((row) => row.current != null),
    [latencyRows],
  );
  const catalogHeaderLabel = catalogColumnLabel(
    showComparison
      ? (mergedMetrics?.currentModelLabel || currentModelLabel)
      : (baseModelName || mergedMetrics?.currentModelLabel || currentModelLabel),
  );
  const comparisonHeaderLabel = catalogColumnLabel(comparisonModelLabel);

  useEffect(() => {
    if (!measuredOutcome?.message || measuredOutcome.tone !== 'error') return;
    if (!liveStatus?.run_id || liveStatus.run_id !== measuredOutcome.runId) return;
    if (toastedWashoutRef.current === liveStatus.run_id) return;
    toastedWashoutRef.current = liveStatus.run_id;
    sparkToast.error(measuredOutcome.message);
  }, [measuredOutcome, liveStatus?.run_id]);

  // Throughput Statistics — derived client-side from the latency rows so we
  // don't need to round-trip a second payload. Only meaningful when more than
  // one request is in flight (concurrency=1 ⇒ throughput = 1/latency, which
  // duplicates the Latency table). Hidden when no latency data is loaded.
  const showThroughput = useMemo(
    () =>
      !isComparisonMode
      && hasActiveFilters
      && Number(filters?.concurrency) > 1
      && Array.isArray(latencyRows)
      && latencyRows.length > 0
      && latencyRows.some((row) => row.current != null || row.measured != null),
    [isComparisonMode, hasActiveFilters, filters?.concurrency, latencyRows],
  );

  const throughputRows = useMemo(
    () =>
      showThroughput
        ? deriveThroughputRowsFromLatency(
          latencyRows,
          Number(filters?.concurrency),
        )
        : [],
    [showThroughput, latencyRows, filters?.concurrency],
  );

  return (
    <div style={{ marginBottom: 20 }}>
      <div
        className="fs-unmask"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          marginBottom: 16,
        }}
      >
        <div>
          <Text variant="h3-bold" style={{ margin: 0 }}>
            Performance Metrics
          </Text>
          <Text variant="detail-regular" color="secondary" style={{ display: 'block', marginTop: 4 }}>
            Catalog is a static platform baseline. Measured columns are serving-perf runs for this version.
          </Text>
        </div>
        <Button
          size="small"
          variant="secondary"
          style={{ marginLeft: 'auto' }}
          onClick={() => setRunModalOpen(true)}
          disabled={!modelVersion}
          disableWhenReadOnly
        >
          Run benchmark
        </Button>
      </div>

      <PerformanceFiltersRow
        filters={filters}
        onOpenFilters={() => setFiltersOpen(true)}
        onClearFilters={handleClear}
        costConfig={costConfig}
        onCostConfigChange={setCostConfig}
        showCostConfig={isComparisonMode}
        showActiveFilters={!isComparisonMode}
      />

      {!isComparisonMode && !hasActiveFilters && (
        <div style={{ marginTop: 8 }}>
          <EmptyStateCard
            onCreateProject={() => setFiltersOpen(true)}
            title="No performance details"
            description="Click 'Open Filter Metrics' to choose a configuration and see performance details."
            primaryActionText="Open Filter Metrics"
            illustrationSrc={sparkAssetUrl('illustrations/illustration-box-open.svg')}
          />
        </div>
      )}

      {!isComparisonMode && hasActiveFilters && (
        <>
          <CostBanner
            instanceType={mergedMetrics?.instanceType || 'persistent'}
            costValue={mergedMetrics?.costValue}
            costConfig={costConfig}
            onChangeCostConfig={setCostConfig}
          />

          <MeasuredRunOutcomeBanner outcome={measuredOutcome} />

          {!loading && latencyRows?.length > 0 && !hasCatalogValues && (
            <Text variant="detail-regular" color="secondary" style={{ display: 'block', marginBottom: 12 }}>
              {catalogEmptyStateText(baseModelName)}
            </Text>
          )}

          <PerformanceStatsTable
            title="Inference Latency Statistics (ms)"
            rows={latencyRows}
            currentModelLabel={catalogHeaderLabel}
            comparisonModelLabel={comparisonHeaderLabel}
            showComparison={showComparison}
            showMeasured={showMeasured}
            measuredLabel={measuredColumnLabel(selectedMeasuredRun)}
            direction="lower-is-better"
            loading={loading}
            emptyText={latencyTableEmptyText(measuredOutcome, baseModelName)}
          />

          {showThroughput && (
            <PerformanceStatsTable
              title="Throughput Statistics (rps)"
              rows={throughputRows}
              currentModelLabel={catalogHeaderLabel}
              comparisonModelLabel={comparisonHeaderLabel}
              showComparison={showComparison}
              showMeasured={showMeasured}
              measuredLabel={measuredColumnLabel(selectedMeasuredRun)}
              direction="higher-is-better"
              loading={loading}
            />
          )}
        </>
      )}

      {isComparisonMode && (
        <InfrastructureComparisonTable
          baseModelName={baseModelName}
          filters={filters}
          costConfig={costConfig}
          currentModelLabel={currentModelLabel}
          comparisonBaseModelName={comparisonBaseModelName}
          comparisonModelKind={comparisonModelKind}
          comparisonModelLabel={comparisonModelLabel}
          taskType={taskType}
        />
      )}

      <MeasuredRunsHistory
        runs={measuredRuns}
        liveStatus={liveStatus}
      />

      <RunBenchmarkModal
        open={runModalOpen}
        onClose={() => setRunModalOpen(false)}
        clientId={clientId}
        llmUsecaseId={llmUsecaseId}
        modelVersion={modelVersion}
        taskType={taskType}
        onQueued={() => setRunsRefreshToken((value) => value + 1)}
      />

      <PerformanceFiltersModal
        open={filtersOpen}
        baseModelName={baseModelName}
        benchmarkAvgTiktoken={benchmarkAvgTiktoken}
        onCancel={() => setFiltersOpen(false)}
        onApply={handleApply}
        value={filters?.environments?.length ? filters : DEFAULT_PERF_FILTERS}
      />
    </div>
  );
}
